//
// Copyright (c) 2011-2026 by Curalate, Inc.
//

import type {
  GetGenerativeAiHashtagExplanationRequest,
  GetGenerativeAiSummaryRequest,
  GetGenerativeAiSummaryRequestBody,
  GetGenerativeAiSummaryResponseData,
  IGetGenerativeAiRightsRequest,
  IGetGenerativeAiRightsResponse,
} from '@apis/generative-ai/generativeAiApiModel';
import { makeParams } from '@utils/makeParams';
import { serializedGatewayFetch } from '@utils/sessionFetch';
import { ISerializedResponse } from '@utils/types/SerializedResponse';

const GENERATIVE_AI_DEFAULT_ENDPOINT = '/api/v1/generative-ai';

const generativeAiFetch = (
  endpoint: string,
  init?: RequestInit,
): Promise<ISerializedResponse> => {
  return serializedGatewayFetch(
    'web_gateway',
    `${GENERATIVE_AI_DEFAULT_ENDPOINT}${endpoint}`,
    init,
  );
};

export const getGenerativeAiRightsRequest = async (
  req: IGetGenerativeAiRightsRequest,
  init?: RequestInit,
): Promise<string> => {
  const params = makeParams(req);
  const res = await generativeAiFetch(`/rights-request?${params}`, init);

  const generativeAiRightsResponse =
    await res.json<IGetGenerativeAiRightsResponse>();

  return generativeAiRightsResponse.data.message;
};

/**
 * Gets a generated explanation for why a hashtag is trending
 *
 * GET /api/v1/generative-ai/trending-hashtag-explanation
 */
export const getGenerativeAiHashtagExplanation = async (
  req: GetGenerativeAiHashtagExplanationRequest,
  init?: RequestInit,
): Promise<string> => {
  const params = makeParams(req);
  const res = await generativeAiFetch(
    `/trending-hashtag-explanation?${params}`,
    init,
  );

  const generativeAiHashtagExplanationResponse = await res.json<{
    data: string;
  }>();

  return generativeAiHashtagExplanationResponse.data;
};

/**
 * Gets a generated summary for a given page
 *
 * POST /api/v1/generative-ai/summaries
 */
export const getGenerativeAiSummary = async (
  req: GetGenerativeAiSummaryRequest,
  body: GetGenerativeAiSummaryRequestBody,
  init?: RequestInit,
): Promise<GetGenerativeAiSummaryResponseData> => {
  const params = makeParams(req);

  const res = await generativeAiFetch(`/summaries?${params}`, {
    ...init,
    method: 'POST',
    body: JSON.stringify(body),
  });

  const { data } = await res.json<{
    data: GetGenerativeAiSummaryResponseData;
  }>();

  return data;
};
